import { clearToken, getToken, setRestrictedToken } from "../auth";

export class ApiError extends Error {
    status: number;
    detail: string;
    // When the backend's ``detail`` field is an object (not a plain string),
    // we stringify it into ``detail`` so existing string consumers keep
    // working, and stash the original here so callers that care, e.g. the
    // Studio A editor surfacing a safety-gate ``findings`` array, can access
    // the structured payload without re-parsing JSON. Null when the detail was
    // a bare string.
    detailPayload: unknown;

    constructor(status: number, detail: string, detailPayload: unknown = null) {
        super(`API ${status}: ${detail}`);
        this.status = status;
        this.detail = detail;
        this.detailPayload = detailPayload;
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function formatValidationLocation(loc: unknown): string {
    if (!Array.isArray(loc)) return "";
    const parts = loc
        .map((part) => String(part))
        .filter((part) => !["body", "query", "path"].includes(part));
    return parts.length > 0 ? `${parts.join(".")}: ` : "";
}

function formatStructuredDetail(detail: unknown): string {
    if (Array.isArray(detail)) {
        const messages = detail
            .map((item) => {
                if (typeof item === "string") return item;
                if (!isRecord(item)) return null;
                const msg = item.msg ?? item.message ?? item.detail;
                if (typeof msg !== "string" || msg.trim() === "") return null;
                return `${formatValidationLocation(item.loc)}${msg}`;
            })
            .filter((msg): msg is string => Boolean(msg));
        if (messages.length > 0) return messages.join("; ");
    }

    if (isRecord(detail) && "message" in detail) {
        return String(detail.message ?? JSON.stringify(detail));
    }

    return JSON.stringify(detail);
}

// All backend API calls go through /api/* so they never collide with SPA
// route paths (/servers, /orgs, /skills, /auth). Vite (dev) and nginx (prod)
// strip the prefix before forwarding to the backend.
export const API_BASE = "/api";

export interface RequestOpts extends RequestInit {
    // If true, skip attaching the Authorization header AND do not clearToken()
    // on 401. Used for public routes where an expired token should not log the
    // user out mid-browse.
    anonymous?: boolean;
    // Explicit bearer to use instead of the stored session token.
    tokenOverride?: string;
}

export function clearUnauthorizedSession(
    response: Pick<Response, "status">, requestToken: string | null, anonymous = false,
): void {
    // A late response for an older login must not clear the current login.
    if (response.status === 401 && !anonymous && getToken() === requestToken) {
        clearToken();
    }
}

export async function request<T>(path: string, init: RequestOpts = {}): Promise<T> {
    const { anonymous, tokenOverride, ...fetchInit } = init;
    const headers = new Headers(fetchInit.headers);
    const token = tokenOverride ?? getToken();
    if (token && !anonymous) headers.set("Authorization", `Bearer ${token}`);
    // Only set JSON content-type when we're actually sending JSON. Multipart
    // uploads must let the browser set the boundary itself.
    if (fetchInit.body && !headers.has("Content-Type") && !(fetchInit.body instanceof FormData)) {
        headers.set("Content-Type", "application/json");
    }

    const url = path.startsWith("/") ? `${API_BASE}${path}` : path;
    const res = await fetch(url, { ...fetchInit, headers });

    clearUnauthorizedSession(res, token, anonymous);

    if (!res.ok) {
        let detail = res.statusText;
        let detailPayload: unknown = null;
        try {
            const body = await res.json();
            if (body?.detail !== undefined) {
                if (typeof body.detail === "string") {
                    detail = body.detail;
                } else {
                    detailPayload = body.detail;
                    detail = formatStructuredDetail(body.detail);
                }
            }
        } catch {
            // body was not json
        }
        if (res.status === 403 && isRecord(detailPayload)
            && detailPayload.code === "mfa_enrollment_required"
            && !anonymous && token && getToken() === token) {
            // Retain only the enrollment proof. The backend limits this old
            // session to setup until the current organization policy is met.
            setRestrictedToken(token);
            clearToken();
            const next = window.location.pathname + window.location.search;
            window.location.assign(`/auth/mfa/enroll-required?next=${encodeURIComponent(next)}`);
        }
        throw new ApiError(res.status, detail, detailPayload);
    }

    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
}
